'use client';

import { c as _c } from "react/compiler-runtime";
import type { Collaborators, Reviewer, ReviewerSelection, TeamReviewerSelection } from '@/types/github';
import { Check, Pencil, Plus, Users } from 'lucide-react';
import { useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { toast } from 'sonner';
import { buildTeamPickerModel, cannotRemoveReviewerMessage, canRemoveReviewer, filterAvailableReviewers, isPendingSelected, sortReviewers, togglePendingSelection } from './utils';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { KeyboardShortcut } from '@/components/ui/keyboard-shortcut';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useCollaborators } from '@/data/collaborators/use-collaborators';
import { useOrgTeams } from '@/data/teams/use-org-teams';
import { trackClientEvent } from '@/lib/analytics/posthog-client';
import { usePrStore } from '@/lib/stores/pr-store';
import { cn } from '@/lib/utils';
type ReviewerButtonClientProps = {
  reviewers: Reviewer[];
  /** Teams currently requested on the PR (slug may be null for legacy data) */
  requestedTeams: Array<{
    name: string;
    slug: string | null;
  }>;
  owner: string;
  repo: string;
  number: number;
  authorLogin: string;
  /** Sends the picker's changes as one batch when the menu closes. */
  batchUpdateReviewers: (update: {
    toAdd: ReviewerSelection[];
    toRemove: ReviewerSelection[];
    teamsToAdd: TeamReviewerSelection[];
    teamsToRemove: TeamReviewerSelection[];
  }) => Promise<void>;
  isBatchUpdating: boolean;
  /** Whether the global E R shortcut opens the picker. Defaults to on. */
  enableShortcut?: boolean;
};
type PendingReviewerChanges = {
  toAdd: Set<string>;
  toRemove: Set<string>;
  // Teams are tracked by slug, which is what the GitHub API takes
  teamsToAdd: Set<string>;
  teamsToRemove: Set<string>;
};
function createEmptyPendingChanges(): PendingReviewerChanges {
  return {
    toAdd: new Set(),
    toRemove: new Set(),
    teamsToAdd: new Set(),
    teamsToRemove: new Set()
  };
}
function TeamCommandItem(t0) {
  const $ = _c(14);
  const {
    team,
    isSelected,
    onSelect
  } = t0;
  const t1 = `team:${team.slug} ${team.name}`;
  const t2 = team.slug;
  let t3;
  if ($[0] !== onSelect || $[1] !== team) {
    t3 = () => onSelect(team);
    $[0] = onSelect;
    $[1] = team;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <span className="flex size-4 items-center justify-center rounded-full bg-zinc-800"><Users className="size-3 text-zinc-400" /></span>;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const t5 = team.name;
  const t6 = isSelected ? "opacity-100" : "opacity-0";
  let t7;
  if ($[4] !== t6) {
    t7 = cn("ml-auto", t6);
    $[4] = t6;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  let t8;
  if ($[6] !== t7) {
    t8 = <Check className={t7} />;
    $[6] = t7;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t9;
  if ($[8] !== t1 || $[9] !== t3 || $[10] !== t8 || $[11] !== team.name || $[12] !== team.slug) {
    t9 = <CommandItem value={t1} key={t2} onSelect={t3} className="flex items-center gap-2">{t4}{t5}{t8}</CommandItem>;
    $[8] = t1;
    $[9] = t3;
    $[10] = t8;
    $[11] = team.name;
    $[12] = team.slug;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  return t9;
}

// Extract the CommandItem into a separate component
function ReviewerCommandItem(t0) {
  const $ = _c(27);
  const {
    reviewer,
    isSelected,
    onSelect,
    canRemove
  } = t0;
  const t1 = reviewer.login;
  const t2 = reviewer.id;
  let t3;
  if ($[0] !== onSelect || $[1] !== reviewer) {
    t3 = () => onSelect(reviewer);
    $[0] = onSelect;
    $[1] = reviewer;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const t4 = !canRemove && isSelected && "cursor-not-allowed opacity-50";
  let t5;
  if ($[3] !== t4) {
    t5 = cn("flex items-center gap-2", t4);
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const t6 = !canRemove && isSelected;
  const t7 = `@${reviewer.login}`;
  let t8;
  if ($[5] !== reviewer.avatar_url || $[6] !== t7) {
    t8 = <AvatarImage src={reviewer.avatar_url} alt={t7} />;
    $[5] = reviewer.avatar_url;
    $[6] = t7;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t9;
  if ($[8] !== reviewer.login) {
    t9 = (reviewer.login ?? "").slice(0, 2).toUpperCase() || "NA";
    $[8] = reviewer.login;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  if ($[10] !== t9) {
    t10 = <AvatarFallback>{t9}</AvatarFallback>;
    $[10] = t9;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== t10 || $[13] !== t8) {
    t11 = <Avatar className="size-4">{t8}{t10}</Avatar>;
    $[12] = t10;
    $[13] = t8;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  const t12 = reviewer.login;
  const t13 = isSelected ? "opacity-100" : "opacity-0";
  let t14;
  if ($[15] !== t13) {
    t14 = cn("ml-auto", t13);
    $[15] = t13;
    $[16] = t14;
  } else {
    t14 = $[16];
  }
  let t15;
  if ($[17] !== t14) {
    t15 = <Check className={t14} />;
    $[17] = t14;
    $[18] = t15;
  } else {
    t15 = $[18];
  }
  let t16;
  if ($[19] !== reviewer.id || $[20] !== reviewer.login || $[21] !== t11 || $[22] !== t15 || $[23] !== t3 || $[24] !== t5 || $[25] !== t6) {
    t16 = <CommandItem value={t1} key={t2} onSelect={t3} className={t5} disabled={t6}>{t11}{t12}{t15}</CommandItem>;
    $[19] = reviewer.id;
    $[20] = reviewer.login;
    $[21] = t11;
    $[22] = t15;
    $[23] = t3;
    $[24] = t5;
    $[25] = t6;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  return t16;
}
export function EditReviewersButton(t0) {
  const $ = _c(27);
  const {
    reviewers,
    requestedTeams,
    owner,
    repo,
    number,
    authorLogin,
    batchUpdateReviewers,
    isBatchUpdating,
    enableShortcut: t1
  } = t0;
  const enableShortcut = t1 === undefined ? true : t1;
  const [open, setOpen] = useState(false);
  const {
    installationId
  } = usePrStore();
  const buttonRef = useRef(null);
  const dropdownContentRef = useRef(null);
  const [pendingChanges, setPendingChanges] = useState(createEmptyPendingChanges);
  const pendingChangesRef = useRef(createEmptyPendingChanges());
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = event => {
      event.preventDefault();
      const button = buttonRef.current;
      if (!button?.offsetParent) {
        return;
      }
      setOpen(true);
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== enableShortcut) {
    t3 = {
      enableOnFormTags: false,
      enabled: enableShortcut
    };
    $[1] = enableShortcut;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  useHotkeys("e>r", t2, t3);
  let t4;
  if ($[3] !== open) {
    t4 = {
      enabled: open
    };
    $[3] = open;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const {
    data: collaborators,
    isError: isCollaboratorsError
  } = useCollaborators(owner, repo, t4);
  let t5;
  if ($[5] !== open) {
    t5 = {
      enabled: open
    };
    $[5] = open;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const {
    data: orgTeams
  } = useOrgTeams(owner, t5);
  const collaboratorList = collaborators ?? [];
  const hasReviewers = reviewers.length > 0;
  let t6;
  if ($[7] !== reviewers) {
    t6 = new Set(reviewers.map(_temp));
    $[7] = reviewers;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const reviewerLogins = t6;
  const reviewersByLogin = new Map(reviewers.map(_temp2));
  const collaboratorsByLogin = new Map(collaboratorList.map(_temp3));
  const {
    sortedTeams,
    requestedTeamSlugs,
    teamsBySlug
  } = buildTeamPickerModel(requestedTeams, orgTeams ?? []);
  const applyPendingChanges = newChanges => {
    pendingChangesRef.current = newChanges;
    setPendingChanges(newChanges);
  };
  const handleReviewerSelect = collaborator_0 => {
    const reviewerLogin = collaborator_0.login;
    const existingReviewer = reviewersByLogin.get(reviewerLogin);
    const previousChanges = pendingChangesRef.current;
    const isBaseSelected = reviewerLogins.has(reviewerLogin);
    if (isPendingSelected(previousChanges, reviewerLogin, isBaseSelected) && existingReviewer && !canRemoveReviewer(existingReviewer)) {
      toast.error(cannotRemoveReviewerMessage(collaborator_0.login));
      return;
    }
    const toggled = togglePendingSelection(previousChanges, reviewerLogin, isBaseSelected);
    applyPendingChanges({
      ...previousChanges,
      ...toggled
    });
  };
  const handleTeamSelect = team => {
    const previousChanges_0 = pendingChangesRef.current;
    const toggled_0 = togglePendingSelection({
      toAdd: previousChanges_0.teamsToAdd,
      toRemove: previousChanges_0.teamsToRemove
    }, team.slug, requestedTeamSlugs.has(team.slug));
    applyPendingChanges({
      ...previousChanges_0,
      teamsToAdd: toggled_0.toAdd,
      teamsToRemove: toggled_0.toRemove
    });
  };
  const commitPendingChanges = changes => {
    if (changes.toAdd.size === 0 && changes.toRemove.size === 0 && changes.teamsToAdd.size === 0 && changes.teamsToRemove.size === 0) {
      return;
    }
    const addedReviewers = Array.from(changes.toAdd);
    const removedReviewers = Array.from(changes.toRemove);
    const resolveTeam = slug => teamsBySlug.get(slug) ?? {
      slug,
      name: slug
    };
    const addedTeams = Array.from(changes.teamsToAdd).map(resolveTeam);
    const removedTeams = Array.from(changes.teamsToRemove).map(resolveTeam);
    trackClientEvent("pr_reviewers_updated", installationId?.toString(), {
      pr_number: number,
      repo: `${owner}/${repo}`,
      reviewers_added: addedReviewers.length,
      reviewers_removed: removedReviewers.length,
      added_reviewers: addedReviewers,
      removed_reviewers: removedReviewers,
      teams_added: addedTeams.length,
      teams_removed: removedTeams.length,
      added_teams: addedTeams.map(_temp4),
      removed_teams: removedTeams.map(_temp5)
    });
    batchUpdateReviewers({
      toAdd: addedReviewers.map(reviewerLogin_0 => ({
        login: reviewerLogin_0,
        avatar_url: collaboratorsByLogin.get(reviewerLogin_0)?.avatar_url ?? reviewersByLogin.get(reviewerLogin_0)?.avatarUrl ?? ""
      })),
      toRemove: removedReviewers.map(reviewerLogin_1 => ({
        login: reviewerLogin_1,
        avatar_url: collaboratorsByLogin.get(reviewerLogin_1)?.avatar_url ?? reviewersByLogin.get(reviewerLogin_1)?.avatarUrl ?? ""
      })),
      teamsToAdd: addedTeams,
      teamsToRemove: removedTeams
    });
  };
  const handleOpenChange = isOpen => {
    if (!isOpen) {
      commitPendingChanges(pendingChangesRef.current);
      const emptyChanges = createEmptyPendingChanges();
      pendingChangesRef.current = emptyChanges;
      setPendingChanges(emptyChanges);
    }
    setOpen(isOpen);
  };
  let t7;
  if ($[9] !== open) {
    t7 = {
      enableOnFormTags: true,
      enabled: open
    };
    $[9] = open;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  useHotkeys("mod+enter", event_0 => {
    if (!open) {
      return;
    }
    const dropdownContent = dropdownContentRef.current;
    const selectedItem = dropdownContent?.querySelector("[cmdk-item][data-selected=\"true\"]") ?? dropdownContent?.querySelector("[cmdk-item]");
    if (!selectedItem) {
      return;
    }
    event_0.preventDefault();
    selectedItem.click();
    handleOpenChange(false);
  }, t7, [handleOpenChange, open]);
  const availableReviewers = filterAvailableReviewers(collaboratorList, authorLogin);
  const sortedReviewers = sortReviewers(reviewers, availableReviewers);
  let t8;
  if ($[11] !== pendingChanges || $[12] !== reviewerLogins) {
    t8 = reviewer_1 => isPendingSelected(pendingChanges, reviewer_1.login, reviewerLogins.has(reviewer_1.login));
    $[11] = pendingChanges;
    $[12] = reviewerLogins;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const isEffectivelySelected = t8;
  const isTeamEffectivelySelected = team_2 => isPendingSelected({
    toAdd: pendingChanges.teamsToAdd,
    toRemove: pendingChanges.teamsToRemove
  }, team_2.slug, requestedTeamSlugs.has(team_2.slug));
  let t9;
  if ($[14] !== hasReviewers) {
    t9 = hasReviewers ? <Pencil className="size-4" /> : <Plus className="size-4" />;
    $[14] = hasReviewers;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== isBatchUpdating || $[17] !== t9) {
    t10 = <DropdownMenuTrigger asChild={true} className="-mt-0.5 ml-0.5"><Button ref={buttonRef} variant="ghost" size="icon" className="size-6 text-zinc-500 hover:text-zinc-300" role="combobox" disabled={isBatchUpdating}>{t9}</Button></DropdownMenuTrigger>;
    $[16] = isBatchUpdating;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <CommandInput placeholder="Search reviewers..." className="h-9" autoFocus={true} />;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const t12 = collaborators ? "No reviewers found." : isCollaboratorsError ? "Could not load reviewers." : "Loading reviewers\u2026";
  let t13;
  if ($[20] !== t12) {
    t13 = <CommandEmpty>{t12}</CommandEmpty>;
    $[20] = t12;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  const t14 = hasReviewers ? "Edit reviewers" : "Add reviewers";
  let t15;
  if ($[22] !== enableShortcut) {
    t15 = enableShortcut ? <KeyboardShortcut>E R</KeyboardShortcut> : null;
    $[22] = enableShortcut;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  let t16;
  if ($[24] !== t14 || $[25] !== t15) {
    t16 = <TooltipContent><div className="flex items-center justify-center gap-2">{t14}{t15}</div></TooltipContent>;
    $[24] = t14;
    $[25] = t15;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  return <TooltipProvider><Tooltip><TooltipTrigger asChild={true}><div><DropdownMenu open={open} onOpenChange={handleOpenChange}>{t10}<DropdownMenuContent ref={dropdownContentRef} className="w-64" align="start" side="left"><Command>{t11}<CommandList>{t13}<CommandGroup>{sortedReviewers.map(reviewer_2 => {
                      const existingReviewer_0 = reviewersByLogin.get(reviewer_2.login);
                      const canRemove = !existingReviewer_0 || canRemoveReviewer(existingReviewer_0);
                      return <ReviewerCommandItem key={reviewer_2.id} reviewer={reviewer_2} isSelected={isEffectivelySelected(reviewer_2)} onSelect={handleReviewerSelect} canRemove={canRemove} />;
                    })}</CommandGroup>{sortedTeams.length > 0 && <CommandGroup heading="Teams">{sortedTeams.map(team_3 => <TeamCommandItem key={team_3.slug} team={team_3} isSelected={isTeamEffectivelySelected(team_3)} onSelect={handleTeamSelect} />)}</CommandGroup>}</CommandList></Command></DropdownMenuContent></DropdownMenu></div></TooltipTrigger>{t16}</Tooltip></TooltipProvider>;
}
function _temp5(team_1) {
  return team_1.slug;
}
function _temp4(team_0) {
  return team_0.slug;
}
function _temp3(collaborator) {
  return [collaborator.login, collaborator];
}
function _temp2(reviewer_0) {
  return [reviewer_0.login, reviewer_0];
}
function _temp(reviewer) {
  return reviewer.login;
}